import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import Badge from 'js/design_system/Badge';
import { ClaimJudgement } from '../api';

const RowContainer = styled.div`
  border: 1px solid var(--stroke-inactive);
  border-radius: var(--border-radius-radius-sm);
  background: var(--background-white);
  margin-bottom: var(--spacing-xs-8);
  overflow: hidden;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: var(--stroke-light);
  }

  &.loading {
    border-color: var(--brand-colors-blue-200);
    background: var(--brand-colors-blue-100);
  }

  &.evaluated {
    animation: fadeIn 0.3s ease-out;
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12);
  padding: var(--spacing-xs-12) var(--spacing-xs-16);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--brand-colors-neutral-300);
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-8);
  flex: 1;
  min-width: 0;
`;

const ClaimNumber = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Separator = styled.span`
  color: var(--text-placeholder);
  font-size: 16px;
`;

const JudgementBadge = styled.div`
  flex-shrink: 0;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--text-inactive);
  transition: color 0.15s ease;

  &:hover {
    color: var(--text-secondary);
  }
`;

const ExpandIconWrapper = styled.div`
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;

  &.expanded {
    transform: rotate(180deg);
  }
`;

const DetailsSection = styled('div')<{ expanded: boolean }>`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;

  &.expanded {
    max-height: 2000px;
  }
`;

const DetailsContent = styled.div`
  padding: var(--spacing-sm-24);
  border-top: 1px solid var(--stroke-inactive);
  background: var(--brand-colors-neutral-200);
`;

const DetailsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16);
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: var(--spacing-xs-16);
  align-items: start;
`;

const DetailLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  padding-top: 2px;
`;

const DetailValue = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-primary);
  word-break: break-word;

  &.quote {
    font-style: italic;
    color: var(--text-secondary);
  }

  &.placeholder {
    color: var(--text-placeholder);
  }
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  color: var(--text-link);
  text-decoration: none;
  font-size: 13px;
  padding: var(--spacing-xs-8) var(--spacing-xs-12);
  background: var(--background-white);
  border-radius: var(--border-radius-radius-sm);
  border: 1px solid var(--stroke-light);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: var(--brand-colors-blue-100);
    border-color: var(--brand-colors-blue-300);
    text-decoration: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  padding: var(--spacing-xs-12);
`;

const LoadingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--brand-colors-blue-100);
  color: var(--brand-colors-blue-600);
  border-radius: var(--border-radius-radius-round);
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: var(--brand-colors-blue-500);
    border-radius: 50%;
    animation: pulse 1s ease-in-out infinite;
  }
`;

const LoadingClaimText = styled(Body)`
  flex: 1;
  min-width: 0;
  color: var(--text-secondary);
`;

const PendingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  padding: var(--spacing-xs-12);
  color: var(--text-inactive);
`;

const SkeletonBadge = styled.div`
  width: 80px;
  height: 22px;
  background: linear-gradient(
    90deg,
    var(--brand-colors-neutral-300) 25%,
    var(--brand-colors-neutral-200) 50%,
    var(--brand-colors-neutral-300) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: var(--border-radius-radius-round);
`;

export type ClaimEvaluationData = {
  claim_text: string;
  reasoning: string;
  quote: string | null;
  supported: ClaimJudgement;
  source_url?: string | null;
};

type ClaimEvaluationRowProps = {
  index: number;
  claimText: string;
  evaluation?: ClaimEvaluationData;
  isLoading?: boolean;
};

const JUDGEMENT_CONFIG: Record<
  ClaimJudgement,
  { backgroundColor: string; color: string; label: string; icon: string }
> = {
  supported: {
    backgroundColor: 'var(--brand-colors-success-500)',
    color: 'white',
    label: 'Supported',
    icon: 'check_circle',
  },
  not_supported: {
    backgroundColor: 'var(--brand-colors-error-500)',
    color: 'white',
    label: 'Not Supported',
    icon: 'cancel',
  },
  maybe_supported: {
    backgroundColor: 'var(--brand-colors-yellow)',
    color: 'var(--text-primary)',
    label: 'Needs Review',
    icon: 'help_outline',
  },
  partially_supported: {
    backgroundColor: 'var(--brand-colors-yellow)',
    color: 'var(--text-primary)',
    label: 'Partially Supported',
    icon: 'help_outline',
  },
  could_not_find_support: {
    backgroundColor: 'var(--brand-colors-neutral-300)',
    color: 'var(--text-secondary)',
    label: 'Could Not Verify',
    icon: 'help_outline',
  },
};

export const ClaimEvaluationRow: React.FC<ClaimEvaluationRowProps> = ({
  index,
  claimText,
  evaluation,
  isLoading = false,
}) => {
  const [expanded, setExpanded] = useState(true);

  // Loading state - actively evaluating this claim
  if (isLoading && !evaluation) {
    return (
      <RowContainer className="loading">
        <LoadingContainer>
          <LoadingBadge>Evaluating</LoadingBadge>
          <LoadingClaimText size="sm">{claimText}</LoadingClaimText>
        </LoadingContainer>
      </RowContainer>
    );
  }

  // Pending evaluation - waiting to be evaluated
  if (!evaluation) {
    return (
      <RowContainer>
        <PendingContainer>
          <SkeletonBadge />
          <Body size="sm" color="tertiary">
            {claimText}
          </Body>
        </PendingContainer>
      </RowContainer>
    );
  }

  const config = JUDGEMENT_CONFIG[evaluation.supported];

  return (
    <RowContainer className="evaluated">
      <RowHeader onClick={() => setExpanded(!expanded)}>
        <HeaderLeft>
          <ClaimNumber>Claim {index + 1}</ClaimNumber>
          <Separator>-</Separator>
        </HeaderLeft>

        <HeaderRight>
          <JudgementBadge>
            <Badge
              text={config.label}
              icon={config.icon}
              iconSize={12}
              backgroundColor={config.backgroundColor}
              color={config.color}
            />
          </JudgementBadge>

          <ExpandButton type="button" aria-label="Toggle details">
            <ExpandIconWrapper className={expanded ? 'expanded' : ''}>
              <Icon name="expand_more" size={18} />
            </ExpandIconWrapper>
          </ExpandButton>
        </HeaderRight>
      </RowHeader>

      <DetailsSection expanded={expanded}>
        <DetailsContent>
          <DetailsGrid>
            <DetailRow>
              <DetailLabel>Claim:</DetailLabel>
              <DetailValue>{claimText}</DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Explanation:</DetailLabel>
              <DetailValue>
                {evaluation.reasoning || <span className="placeholder">—</span>}
              </DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Supporting Quote:</DetailLabel>
              <DetailValue>
                {evaluation.quote ? (
                  <span className="quote">"{evaluation.quote}"</span>
                ) : (
                  <span className="placeholder">—</span>
                )}
              </DetailValue>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Source:</DetailLabel>
              <DetailValue>
                {evaluation.source_url ? (
                  <SourceLink
                    href={evaluation.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Icon name="open_in_new" size={14} />
                    View Retrieved Text
                  </SourceLink>
                ) : (
                  <span className="placeholder">—</span>
                )}
              </DetailValue>
            </DetailRow>
          </DetailsGrid>
        </DetailsContent>
      </DetailsSection>
    </RowContainer>
  );
};

export default ClaimEvaluationRow;
